// components/NewtonSolverExplanation.js
import React from 'react';

export default function NewtonSolverExplanation() {
  return (
    <div className="w-full p-1 text-xs text-gray-800 text-justify leading-relaxed hyphens-auto md:text-sm">
      <p><strong>Goal:</strong> Find a solution <code>x ∈ ℝⁿ</code> for f(x) = 0,
        where <code>f: ℝⁿ → ℝⁿ</code> is a vector valued function
        representing a system of nonlinear equations.</p>

      <p className="mt-3"><strong>Mathematical explanation</strong></p>
      In the k'th iteration, given the current iterate <code>xₖ</code>:
      <ol className="list-decimal pl-5 space-y-2 mb-4">
        <li>
          <strong>Jacobian evaluation:</strong> Evaluate the Jacobian <code>J(xₖ) ∈ ℝⁿˣⁿ</code> containing
          all partial derivatives of f at the current point <code>xₖ</code>.
        </li>

        <li>
          <strong>Linear solve:</strong> Solve the linear system for the update step <code>Δx</code>:
          <pre className="bg-white p-2 border border-gray-200 rounded text-xs mb-1 flex items-center justify-center text-center min-h-[40px]">
            J(xₖ) · Δx = -f(xₖ)
          </pre>
        </li>

        <li>
          <strong>Update step:</strong> Compute the next iterate <code>xₖ₊₁</code> using the update step <code>Δx</code>
          and a damping factor <code>λ ∈ [0, 1]</code> if damping is enabled:
          <pre className="bg-white p-2 border border-gray-200 rounded text-xs mb-1 flex items-center justify-center text-center min-h-[40px]">
            xₖ₊₁ = xₖ + λ · Δx
          </pre>
        </li>

        <li>
          <strong>Convergence check:</strong> Stop if the norm of <code>f(xₖ₊₁)</code> is smaller than the
          tolerance <code>ε</code> or if the maximum number of iterations is reached.
        </li>
      </ol>
      <p className="mb-2"><strong>Damping:</strong> If enabled, the update step is scaled by a factor λ between 0 and 1
        to stabilize convergence for difficult problems.</p>

      <p className="mb-2"><strong>Variants:</strong> In addition to the classic Newton method, the solver also supports:</p>
      <ul className="list-disc pl-5 space-y-1 mb-4">
        <li>
          <strong>Quasi-Newton:</strong> Avoids computing the full Jacobian in each step by approximating it iteratively
          via Broyden's method.
        </li>
        <li>
          <strong>Gauss-Newton:</strong> A variant for solving nonlinear least squares problems of the form <code>f(x) ≈ 0</code>,
          where <code>f</code> is a vector of residuals.
          It approximates the Hessian using the transposed Jacobian.
        </li>
      </ul>

      <p className="mt-4"><strong>Input format</strong> </p>
      <ul className="list-disc pl-5 space-y-1 mb-2">
        <li><strong>Function:</strong> Provide one or more expressions using <code>x[0]</code>, <code>x[1]</code>, etc.
          Even for one dimensional problems use <code>x[0]</code>.</li>
        <li><strong>Initial guess:</strong> A list of floats, e.g. <code>[1.0, 2.0]</code></li>

        <li>Use Python-style math syntax: <code>**</code> for powers, and functions like <code>sin</code>, <code>log</code>, <code>exp</code>, <code>sqrt</code>.</li>
        <li>Separate multiple functions with commas.</li>

      </ul>
      <pre className="bg-white p-2 border border-gray-200 rounded text-xs mt-1 mb-2">
        f(x): x[0]**2 + x[1] - 2, 
        <br />      x[0] + x[1]**2 - 2<br />
        x₀: [1.0, 2.0]
      </pre>

      <p className="mt-4 font-semibold">Output format</p>
      <ul className="list-disc pl-5 mb-4">
        <li>Final solution vector <code>x</code>.</li>
        <li>Convergence status: "success" if converged, "failure" if not.</li>
        <li>Number of iterations taken to converge.</li>
        <li>Final residual value <code>f(x)</code> at the solution.</li>
        <li>A visual plot of the residual for each iteration step.</li>
      </ul>
    </div>
  );
}
